import { Copy } from 'lucide-react'
import { Controller, useFormContext } from 'react-hook-form'

import { useActiveTenant } from '../../../hooks/common/useActiveTenant'
import FloatingLabel from '../../inputs/FloatingLabel'
import LabeledInputText from '../../inputs/LabeledInputText'
import CustomFileUpload from '../common/CustomFileUpload'


import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { showToast } from '@/utils/toastMessage'

const TenantInfo = () => {
  const { VITE_APP_PUBLIC_CUSTOMER_UI_V2_URL } = import.meta.env

  const { control } = useFormContext()
  const { tenant } = useActiveTenant()

  const reservationLink = `${VITE_APP_PUBLIC_CUSTOMER_UI_V2_URL}?tenantId=${tenant?.tenantId}`

  const copyToClipboard = (text: string) => {
    if (text) {
      navigator.clipboard.writeText(text)
      showToast({ severity: 'info', detail: 'Link wurde in die Zwischenablage kopiert.' })
    }
  }

  return (
    <div className="space-y-10">
      {/* Company Profile Section */}
      <section>
        <div className="mb-6">
          <h2 className="text-lg font-semibold">Unternehmensprofil</h2>
          <p className="text-muted-foreground text-sm">
            Logo, Kontaktinformationen und Adresse Ihres Unternehmens.
          </p>
        </div>

        {/* Mobile: Vertical Stack, Desktop: Logo left, Info right */}
        <div className="flex flex-col gap-8 lg:grid lg:grid-cols-[auto_1fr] lg:gap-8">
          {/* Logo on the left (desktop) / top (mobile) */}
          <div className="flex justify-center lg:justify-start">
            <Controller
              name="tenant.info.logoUrl"
              control={control}
              render={({ field }) => (
                <CustomFileUpload
                  src={field.value}
                  onChange={field.onChange}
                  url="common/management/tenant/image"
                  alt="Unternehmenslogo"
                  chooseLabel="Neues Logo hochladen"
                  message="Neues Logo erfolgreich hochgelden."
                />
              )}
            />
          </div>

          {/* Basic Info and Address side by side on desktop, stacked on mobile */}
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            {/* Basic Information */}
            <div className="space-y-6">
              <div>
                <h3 className="mb-4 text-sm font-medium">Grundinformationen</h3>
                <div className="space-y-4">
                  <Controller
                    control={control}
                    name="tenant.name"
                    render={({ field }) => (
                      <LabeledInputText
                        label="Name"
                        id={field.name}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="tenant.info.mail"
                    render={({ field }) => (
                      <LabeledInputText
                        label="E-Mail"
                        id={field.name}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  <Controller
                    control={control}
                    name="tenant.info.phone"
                    render={({ field }) => (
                      <LabeledInputText
                        label="Telefonnummer"
                        id={field.name}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            {/* Address Information */}
            <div className="space-y-6">
              <div>
                <h3 className="mb-4 text-sm font-medium">Adresse</h3>
                <div className="space-y-4">
                  <Controller
                    control={control}
                    name="tenant.info.street"
                    render={({ field }) => (
                      <LabeledInputText
                        label="Straße"
                        id={field.name}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                  <div className="grid grid-cols-[2fr_3fr] gap-4">
                    <Controller
                      control={control}
                      name="tenant.info.postcode"
                      render={({ field }) => (
                        <LabeledInputText
                          label="PLZ"
                          id={field.name}
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="tenant.info.city"
                      render={({ field }) => (
                        <LabeledInputText
                          label="Stadt"
                          id={field.name}
                          value={field.value}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reservation Link Section */}
      <section>
        <div className="mb-6">
          <h2 className="text-lg font-semibold">Online-Reservierung</h2>
          <p className="text-muted-foreground text-sm">
            Teilen Sie diesen Link mit Ihren Kunden für Online-Reservierungen.
          </p>
        </div>
        <FloatingLabel label="Online-Reservierungslink">
          <div className="flex">
            <Button
              variant="outline"
              className="bg-primary hover:bg-primary rounded-r-none text-white hover:text-white"
              onClick={() => copyToClipboard(reservationLink || '')}
            >
              <Copy className="h-4 w-4" />
            </Button>
            <Input
              id="tenant.reservationLink"
              className="flex-1 cursor-default rounded-l-none"
              type="text"
              value={reservationLink}
              readOnly
            />
          </div>
        </FloatingLabel>
      </section>
    </div>
  )
}

export default TenantInfo
